import { ChangeDetectorRef, Component, inject, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { AppButtonComponent } from "../../../../Common/Components/Button/ButtonComponent";
import { AppCardComponent } from "../../../../Common/Components/Card/CardComponent";
import { LocationSelectorComponent } from "../../../../Common/Components/LocationSelector/LocationSelectorComponent";
import { LocationAddress } from "../../../../Common/Components/LocationSelector/Type/LocationSelectorTypes";
import { RichTextEditorComponent } from "../../../../Common/Components/RichTextEditor/RichTextEditorComponent";
import { ToastEffects } from "../../../../Common/Components/Toast/State/ToastEffects";
import { AutocompleteComponent } from "../../../../Common/Components/Autocomplete/AutocompleteComponent";
import { VendorEffects } from "../../State/VendorEffects";
import { Vendor } from "../../Dto/VendorModel";
import { VendorType } from "../../Dto/VendorModel";
import { getBackendErrorMessage } from "../../../../Common/Utility/UtilityError";
import { CreateVendorRequest } from "../../Dto/CreateVendorRequest";

@Component({
  selector: "app-vendor-edit",
  standalone: true,
  imports: [
    ReactiveFormsModule,
    AppButtonComponent,
    AppCardComponent,
    LocationSelectorComponent,
    RichTextEditorComponent,
    AutocompleteComponent,
  ],
  templateUrl: "./VendorEditPage.html",
  styleUrl: "./VendorEditPage.scss",
})
export class VendorEditPage implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly vendorEffects = inject(VendorEffects);
  private readonly toastEffects = inject(ToastEffects);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly typeOptions = Object.values(VendorType).map((type) => ({
    value: type,
    label: type,
  }));

  readonly vendorId = this.route.snapshot.paramMap.get("id") ?? undefined;
  isSaving = false;
  addressValue?: LocationAddress;
  form: FormGroup;

  constructor() {
    this.form = this.fb.group({
      name: ["", [Validators.required]],
      type: ["", [Validators.required]],
      contactPerson: [""],
      phone: [""],
      email: [""],
      note: [""],
      address: this.fb.group({
        stateProvinceRegion: ["", [Validators.required]],
        countyDistrict: ["", [Validators.required]],
        subdistrictMunicipality: ["", [Validators.required]],
        communeVillage: ["", [Validators.required]],
        line: [""],
        zip: [""],
        code: [""],
        longitude: [null],
        latitude: [null],
      }),
    });
  }

  get canSave(): boolean {
    return !this.isSaving && this.form.valid && !!this.vendorId;
  }

  async ngOnInit(): Promise<void> {
    if (!this.vendorId) {
      await this.router.navigate(["/vendors"]);
      return;
    }

    const vendor = await this.vendorEffects.getVendorById(this.vendorId);
    if (!vendor) {
      this.toastEffects.show("Vendor not found.", "error");
      await this.router.navigate(["/vendors"]);
      return;
    }

    setTimeout(() => {
      const mappedAddress = this.toFormAddress(vendor);
      this.form.patchValue({
        name: vendor.name,
        type: vendor.type,
        contactPerson: vendor.contactPerson ?? "",
        phone: vendor.phone ?? "",
        email: vendor.email ?? "",
        note: vendor.note ?? "",
        address: mappedAddress,
      });
      this.addressValue = mappedAddress;
      this.cdr.detectChanges();
    }, 0);
  }

  async save(): Promise<void> {
    if (!this.vendorId || this.form.invalid) {
      this.form.markAllAsTouched();
      this.toastEffects.show("Please fill in the required fields.", "error");
      return;
    }

    this.isSaving = true;
    this.cdr.detectChanges();
    const formData = this.form.value;
    const payload: CreateVendorRequest = {
      name: formData.name,
      type: formData.type,
      contactPerson: formData.contactPerson || undefined,
      phone: formData.phone || undefined,
      email: formData.email || undefined,
      address: {
        stateProvinceRegion: formData.address.stateProvinceRegion,
        countyDistrict: formData.address.countyDistrict,
        subdistrictMunicipality: formData.address.subdistrictMunicipality,
        communeVillage: formData.address.communeVillage,
        line: formData.address.line || "",
        zip: formData.address.zip || "",
        code: formData.address.code || "",
        longitude: formData.address.longitude ?? null,
        latitude: formData.address.latitude ?? null,
      },
      note: formData.note || undefined,
    };

    try {
      await this.vendorEffects.updateVendor(this.vendorId, payload);

      setTimeout(() => {
        this.toastEffects.show("Vendor updated.", "success");
        this.isSaving = false;
        this.cdr.detectChanges();
        this.router.navigate(["/vendors"]).catch(() => undefined);
      }, 0);
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Unable to update vendor."), "error");
      setTimeout(() => {
        this.isSaving = false;
        this.cdr.detectChanges();
      }, 0);
    }
  }

  goBack(): void {
    this.router.navigate(["/vendors"]).catch(() => undefined);
  }

  onTypeChange(value: string | number | (string | number)[]): void {
    if (Array.isArray(value)) {
      return;
    }

    this.form.patchValue({ type: value.toString() });
  }

  onAddressChange(value: LocationAddress): void {
    this.addressValue = value;
    this.form.patchValue({
      address: {
        stateProvinceRegion: value.stateProvinceRegion,
        countyDistrict: value.countyDistrict,
        subdistrictMunicipality: value.subdistrictMunicipality,
        communeVillage: value.communeVillage,
        line: value.line,
        zip: value.zip,
        code: value.code,
        longitude: value.longitude,
        latitude: value.latitude,
      },
    });
  }

  private toFormAddress(vendor: Vendor): LocationAddress {
    const address = vendor.address;
    if (!address || typeof address === "string") {
      return {
        stateProvinceRegion: "",
        countyDistrict: "",
        subdistrictMunicipality: "",
        communeVillage: "",
        line: (() => {
          if (typeof address === "string") {
            return address;
          }
          return "";
        })(),
        zip: "",
        code: "",
        longitude: null,
        latitude: null,
      };
    }

    return {
      stateProvinceRegion: address.stateProvinceRegion ?? "",
      countyDistrict: address.countyDistrict ?? "",
      subdistrictMunicipality: address.subdistrictMunicipality ?? "",
      communeVillage: address.communeVillage ?? "",
      line: address.line ?? "",
      zip: address.zip ?? "",
      code: address.code ?? "",
      longitude: address.longitude ?? null,
      latitude: address.latitude ?? null,
    };
  }
}
